import { useState, useRef, useEffect } from 'react';
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Firestore,
} from 'firebase/firestore';
import { User, UserRole } from '../../types';
import { Trash2, Upload, CheckCircle, XCircle, AlertCircle, User as UserIcon, Loader2 } from 'lucide-react';

type MemberImportProgress = {
  total: number;
  processed: number;
  imported: number;
  skipped: number;
  failed: Array<{ email: string; reason: string }>;
};

interface MembersTabProps {
  members: Array<User & { role: UserRole; joinedAt: Date; hasAccount?: boolean }>;
  loadingMembers: boolean;
  canEdit: boolean;
  currentUser: any;
  handleChangeMemberRole: (uid: string, newRole: UserRole) => Promise<any>;
  handleRemoveMember: (uid: string, memberName: string) => Promise<any>;
  handleImportMembers?: (
    members: Array<{ firstName: string; lastName: string; email: string }>
  ) => Promise<{ importId: string }>;
  unitId?: string;
  db?: Firestore;
  onImportComplete?: (summary: MemberImportProgress) => void;
  formatDate: (date: Date) => string;
  Card: any;
  CardContent: any;
  CardDescription: any;
  CardHeader: any;
  CardTitle: any;
  Button: any;
  Badge: any;
  Select: any;
  SelectTrigger: any;
  SelectContent: any;
  SelectItem: any;
  SelectValue: any;
  Dialog: any;
  DialogContent: any;
  DialogHeader: any;
  DialogTitle: any;
  DialogDescription: any;
  DialogFooter: any;
  Label: any;
  Input: any;
}

export function MembersTab({
  members,
  loadingMembers,
  canEdit,
  currentUser,
  handleChangeMemberRole,
  handleRemoveMember,
  handleImportMembers,
  unitId,
  db,
  onImportComplete,
  formatDate,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Label,
  Input,
}: MembersTabProps) {
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [hasHeaders, setHasHeaders] = useState(true);
  const [fieldMapping, setFieldMapping] = useState<{ firstName: string; lastName: string; email: string }>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [importing, setImporting] = useState(false);
  const [activeImportId, setActiveImportId] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<MemberImportProgress | null>(null);
  const [importSummary, setImportSummary] = useState<MemberImportProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importCompleteHandledRef = useRef<string | null>(null);

  const resetImportForm = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({ firstName: '', lastName: '', email: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearFinishedImport = () => {
    setActiveImportId(null);
    setImportProgress(null);
    setImportSummary(null);
    setImporting(false);
    resetImportForm();
  };

  useEffect(() => {
    // Reading memberImports requires Secretary/Treasurer/admin per Firestore
    // rules; skip for other roles to avoid a permission-denied error on tab open.
    if (!db || !unitId || !canEdit) {
      return;
    }

    const loadActiveImport = async () => {
      const importsQuery = query(
        collection(db, 'units', unitId, 'memberImports'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const snapshot = await getDocs(importsQuery);
      const activeDoc = snapshot.docs.find((importDoc) => {
        const status = importDoc.data().status;
        return status === 'pending' || status === 'processing';
      });

      if (activeDoc) {
        setActiveImportId(activeDoc.id);
        setImporting(true);
        setImportProgress(activeDoc.data().progress ?? null);
      }
    };

    void loadActiveImport();
  }, [db, unitId, canEdit]);

  useEffect(() => {
    if (!db || !unitId || !activeImportId || !canEdit) {
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'units', unitId, 'memberImports', activeImportId),
      (snapshot) => {
        if (!snapshot.exists()) {
          return;
        }

        const data = snapshot.data();
        const progress = (data.progress as MemberImportProgress | undefined) ?? null;
        setImportProgress(progress);

        if (data.status === 'completed' && progress) {
          setImportSummary(progress);
          setImporting(false);
          if (importCompleteHandledRef.current !== activeImportId) {
            importCompleteHandledRef.current = activeImportId;
            onImportComplete?.(progress);
          }
        } else if (data.status === 'failed') {
          setImportSummary({
            total: progress?.total ?? 0,
            processed: progress?.processed ?? 0,
            imported: progress?.imported ?? 0,
            skipped: progress?.skipped ?? 0,
            failed: [
              ...(progress?.failed ?? []),
              { email: '', reason: (data.error as string) || 'Import failed' },
            ],
          });
          setImporting(false);
        } else if (data.status === 'pending' || data.status === 'processing') {
          setImporting(true);
        }
      }
    );

    return unsubscribe;
  }, [db, unitId, activeImportId, canEdit, onImportComplete]);

  const parseCSV = (text: string): string[][] => {
    const lines: string[][] = [];
    let currentLine: string[] = [];
    let currentField = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentField += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        currentLine.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (currentField || currentLine.length > 0) {
          currentLine.push(currentField.trim());
          currentField = '';
        }
        if (currentLine.length > 0) {
          lines.push(currentLine);
          currentLine = [];
        }
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
      } else {
        currentField += char;
      }
    }

    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField.trim());
    }
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    setImportSummary(null);
    
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        alert('CSV file is empty');
        return;
      }

      // Determine headers and data based on hasHeaders checkbox
      let headers: string[];
      let dataRows: string[][];
      
      if (hasHeaders && rows.length > 0) {
        headers = rows[0];
        dataRows = rows.slice(1);
      } else {
        // No headers - use column indices as headers (Column 1, Column 2, etc.)
        if (rows.length > 0) {
          const maxColumns = Math.max(...rows.map(row => row.length));
          headers = Array.from({ length: maxColumns }, (_, i) => `Column ${i + 1}`);
          dataRows = rows;
        } else {
          headers = [];
          dataRows = [];
        }
      }

      setCsvHeaders(headers);
      setCsvData(dataRows);

      // Auto-detect field mapping
      const autoMapping: { firstName: string; lastName: string; email: string } = {
        firstName: '',
        lastName: '',
        email: '',
      };

      headers.forEach((header) => {
        const lowerHeader = header.toLowerCase().trim();
        // Check for first name
        if ((lowerHeader.includes('first') && lowerHeader.includes('name')) || 
            (lowerHeader === 'first' || lowerHeader.startsWith('first'))) {
          if (!autoMapping.firstName) {
            autoMapping.firstName = header;
          }
        }
        // Check for last name
        else if ((lowerHeader.includes('last') && lowerHeader.includes('name')) || 
                 (lowerHeader === 'last' || lowerHeader.startsWith('last'))) {
          if (!autoMapping.lastName) {
            autoMapping.lastName = header;
          }
        }
        // Check for email - look for "email" keyword OR if it looks like an email (contains @)
        // But prioritize columns that contain "email" in the name
        else if (lowerHeader.includes('email')) {
          if (!autoMapping.email) {
            autoMapping.email = header;
          }
        }
      });
      
      // If email not found by keyword, look for columns that look like emails
      if (!autoMapping.email) {
        headers.forEach((header) => {
          // Check if header itself looks like an email (but be cautious - this could be data)
          // Only use this as a fallback
          if (header.includes('@') && header.includes('.') && header.length > 5 && header.length < 100) {
            // This might be an email header, but also might be data
            // Only use if we haven't found anything else
            if (!autoMapping.email) {
              autoMapping.email = header;
            }
          }
        });
      }
      
      // If still no email found, try to find the column that contains emails in the data
      if (!autoMapping.email && dataRows.length > 0) {
        // Check each column to see if it contains email-like values
        for (let colIdx = 0; colIdx < headers.length; colIdx++) {
          const sampleValues = dataRows.slice(0, Math.min(3, dataRows.length)).map(row => row[colIdx] || '');
          const emailLikeCount = sampleValues.filter(val => 
            val.includes('@') && val.includes('.') && val.length > 5
          ).length;
          
          // If most sample values look like emails, this is probably the email column
          if (emailLikeCount >= Math.min(2, sampleValues.length)) {
            autoMapping.email = headers[colIdx];
            break;
          }
        }
      }

      setFieldMapping(autoMapping);
    } catch (error) {
      console.error('Error reading CSV file:', error);
      alert('Failed to read CSV file');
    }
  };

  const handleHasHeadersChange = (checked: boolean) => {
    setHasHeaders(checked);
    
    // Re-process the file when header option changes
    if (csvFile) {
      // Re-read and parse the file with new header setting
      csvFile.text().then((text) => {
        const rows = parseCSV(text);
        
        if (rows.length === 0) {
          return;
        }

        // Determine headers and data based on hasHeaders checkbox
        let headers: string[];
        let dataRows: string[][];
        
        if (checked && rows.length > 0) {
          headers = rows[0];
          dataRows = rows.slice(1);
        } else {
          // No headers - use column indices as headers (Column 1, Column 2, etc.)
          if (rows.length > 0) {
            const maxColumns = Math.max(...rows.map(row => row.length));
            headers = Array.from({ length: maxColumns }, (_, i) => `Column ${i + 1}`);
            dataRows = rows;
          } else {
            headers = [];
            dataRows = [];
          }
        }

        setCsvHeaders(headers);
        setCsvData(dataRows);

        // Auto-detect field mapping
        const autoMapping: { firstName: string; lastName: string; email: string } = {
          firstName: '',
          lastName: '',
          email: '',
        };

        headers.forEach((header) => {
          const lowerHeader = header.toLowerCase().trim();
          // Check for first name
          if ((lowerHeader.includes('first') && lowerHeader.includes('name')) || 
              (lowerHeader === 'first' || lowerHeader.startsWith('first'))) {
            if (!autoMapping.firstName) {
              autoMapping.firstName = header;
            }
          }
          // Check for last name
          else if ((lowerHeader.includes('last') && lowerHeader.includes('name')) || 
                   (lowerHeader === 'last' || lowerHeader.startsWith('last'))) {
            if (!autoMapping.lastName) {
              autoMapping.lastName = header;
            }
          }
          // Check for email - look for "email" keyword first
          else if (lowerHeader.includes('email')) {
            if (!autoMapping.email) {
              autoMapping.email = header;
            }
          }
        });
        
        // If email not found by keyword, look for columns that look like emails in headers
        if (!autoMapping.email) {
          headers.forEach((header) => {
            // Check if header itself looks like an email (but be cautious - this could be data)
            // Only use this as a fallback
            if (header.includes('@') && header.includes('.') && header.length > 5 && header.length < 100) {
              // This might be an email header, but also might be data
              // Only use if we haven't found anything else
              if (!autoMapping.email) {
                autoMapping.email = header;
              }
            }
          });
        }
        
        // If still no email found, try to find the column that contains emails in the data
        if (!autoMapping.email && dataRows.length > 0) {
          // Check each column to see if it contains email-like values
          for (let colIdx = 0; colIdx < headers.length; colIdx++) {
            const sampleValues = dataRows.slice(0, Math.min(3, dataRows.length)).map(row => row[colIdx] || '');
            const emailLikeCount = sampleValues.filter(val => 
              val.includes('@') && val.includes('.') && val.length > 5
            ).length;
            
            // If most sample values look like emails, this is probably the email column
            if (emailLikeCount >= Math.min(2, sampleValues.length)) {
              autoMapping.email = headers[colIdx];
              break;
            }
          }
        }

        setFieldMapping(autoMapping);
      }).catch((error) => {
        console.error('Error re-reading CSV file:', error);
      });
    }
  };

  const handleImport = async () => {
    if (!handleImportMembers || !fieldMapping.firstName || !fieldMapping.lastName || !fieldMapping.email) {
      alert('Please map all required fields: First name, Last name, and Email address');
      return;
    }

    if (csvData.length === 0) {
      alert('No data to import');
      return;
    }

    if (importRunning) {
      alert('A member import is already in progress for this unit.');
      return;
    }

    setImporting(true);
    try {
      const firstNameIdx = csvHeaders.indexOf(fieldMapping.firstName);
      const lastNameIdx = csvHeaders.indexOf(fieldMapping.lastName);
      const emailIdx = csvHeaders.indexOf(fieldMapping.email);

      if (firstNameIdx === -1 || lastNameIdx === -1 || emailIdx === -1) {
        alert('Invalid field mapping. Please ensure all fields are mapped correctly.');
        console.error('Field mapping indices:', { firstNameIdx, lastNameIdx, emailIdx });
        console.error('CSV headers:', csvHeaders);
        console.error('Field mapping:', fieldMapping);
        return;
      }

      const membersToImport = csvData
        .map((row, index) => {
          const firstName = (row[firstNameIdx] || '').trim();
          const lastName = (row[lastNameIdx] || '').trim();
          const email = (row[emailIdx] || '').trim().toLowerCase();
          
          // Debug logging
          if (index < 3) {
            console.log(`Row ${index}:`, { firstName, lastName, email, row });
          }
          
          return {
            firstName,
            lastName,
            email,
          };
        })
        .filter((m) => {
          // Filter out rows with empty emails or invalid emails
          if (!m.email || !m.email.includes('@')) {
            console.warn('Filtered out invalid email:', m);
            return false;
          }
          return true;
        });

      console.log('Members to import:', membersToImport);

      if (membersToImport.length === 0) {
        alert('No valid members to import. Please check that your CSV has valid email addresses.');
        return;
      }

      const { importId } = await handleImportMembers(membersToImport);
      setActiveImportId(importId);
      setImportSummary(null);
      setImportProgress({
        total: membersToImport.length,
        processed: 0,
        imported: 0,
        skipped: 0,
        failed: [],
      });
    } catch (error) {
      console.error('Error importing members:', error);
      setImporting(false);
      if (!(error instanceof Error && error.message.includes('already running'))) {
        alert('Failed to start member import. Check the console for details.');
      }
    }
  };

  const importRunning = importing || (!!activeImportId && !importSummary);
  const progressPercent =
    importProgress && importProgress.total > 0
      ? Math.round((importProgress.processed / importProgress.total) * 100)
      : 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Members</CardTitle>
              <CardDescription>
                Manage unit members and their roles. You can change member roles or remove their
                access completely.
              </CardDescription>
            </div>
            {canEdit && handleImportMembers && (
              <Button onClick={() => setImportModalOpen(true)} variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Import
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
        {loadingMembers ? (
          <div className="text-center py-8">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading members...</p>
          </div>
        ) : (
          <>
            {canEdit && importRunning && (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-blue-700" />
                  <div className="flex-1 space-y-2">
                    <p className="text-sm font-medium text-blue-900">
                      Member import in progress
                    </p>
                    <p className="text-sm text-blue-800">
                      {importProgress
                        ? `${importProgress.processed} of ${importProgress.total} processed (${importProgress.imported} imported)`
                        : 'Starting import...'}
                    </p>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-blue-100">
                      <div
                        className="h-full rounded-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <Button
                      variant="link"
                      className="h-auto p-0 text-blue-900"
                      onClick={() => setImportModalOpen(true)}
                    >
                      View import details
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {canEdit && (
              <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-4">
                <p className="text-sm text-blue-900">
                  <strong>Member Management:</strong> You can change member roles using the
                  dropdown menu. To remove a member's access entirely, click the{' '}
                  <strong>"Remove"</strong> button. Removed members will lose all
                  access to this unit, including events and RSVPs.
                </p>
              </div>
            )}
            <div className="space-y-2">
              {members.map((member) => (
                <div
                  key={member.uid}
                  className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <span>{member.name}</span>
                      {member.hasAccount === true && (
                        <span title="Has account on Masonic Tools">
                          <UserIcon className="h-4 w-4 text-blue-600" />
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                      {member.email}
                      {member.hasAccount !== undefined && (
                        <Badge variant={member.hasAccount ? 'default' : 'secondary'} className="text-xs">
                          {member.hasAccount ? 'Has Account' : 'No Account'}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Joined {formatDate(member.joinedAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canEdit && member.hasAccount !== false ? (
                      <Select
                        value={member.role}
                        onValueChange={(value: UserRole) =>
                          handleChangeMemberRole(member.uid, value)
                        }
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Guest">Guest</SelectItem>
                          <SelectItem value="Member">Member</SelectItem>
                          <SelectItem value="Steward">Steward</SelectItem>
                          <SelectItem value="Treasurer">Treasurer</SelectItem>
                          <SelectItem value="Secretary">Secretary</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <>
                        <Badge>{member.role}</Badge>
                        {canEdit && member.hasAccount === false && (
                          <span className="text-xs text-muted-foreground italic">
                            (Cannot change role until account is created)
                          </span>
                        )}
                      </>
                    )}
                    {canEdit && currentUser && member.uid !== currentUser.uid ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveMember(member.uid, member.name)}
                        className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        Remove
                      </Button>
                    ) : currentUser && member.uid === currentUser.uid ? (
                      <Badge variant="secondary" className="text-xs">
                        You
                      </Badge>
                    ) : null}
                  </div>
                </div>
              ))}

              {members.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No members found
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>

    {Dialog && DialogContent && DialogHeader && DialogTitle && DialogDescription && DialogFooter && (
      <Dialog
        open={importModalOpen}
        onOpenChange={(open: boolean) => {
          setImportModalOpen(open);
          if (!open && !importRunning) {
            if (importSummary) {
              clearFinishedImport();
            } else {
              resetImportForm();
              setImportSummary(null);
            }
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Members from CSV</DialogTitle>
            <DialogDescription>
              {importRunning
                ? 'Import runs in the background. You can close this dialog and continue using the site.'
                : 'Upload a CSV file and map the columns to import members. Members will be added with the "Member" role.'}
            </DialogDescription>
          </DialogHeader>

          {importSummary ? (
            <>
              <div className="space-y-4 py-4">
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Imported: {importSummary.imported}</span>
                  </div>
                  <div className="flex items-center gap-2 text-yellow-600">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Skipped: {importSummary.skipped}</span>
                  </div>
                  {importSummary.failed.length > 0 && (
                    <div className="flex items-start gap-2 text-red-600">
                      <XCircle className="h-5 w-5 mt-0.5" />
                      <div className="flex-1">
                        <span className="font-medium">Failed: {importSummary.failed.length}</span>
                        <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                          {importSummary.failed.map((failure, idx) => (
                            <div key={idx} className="text-xs">
                              <span className="font-medium">{failure.email}:</span> {failure.reason}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button
                  onClick={() => {
                    setImportModalOpen(false);
                    clearFinishedImport();
                  }}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : importRunning ? (
            <>
              <div className="space-y-4 py-4">
                <div className="rounded-lg border p-4 space-y-4">
                  <div className="flex items-center gap-2 text-blue-700">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="font-medium">Processing member import</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {importProgress
                      ? `${importProgress.processed} of ${importProgress.total} rows processed`
                      : 'Preparing import...'}
                  </p>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  {importProgress && (
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div>
                        <p className="font-medium text-green-600">{importProgress.imported}</p>
                        <p className="text-muted-foreground">Imported</p>
                      </div>
                      <div>
                        <p className="font-medium text-yellow-600">{importProgress.skipped}</p>
                        <p className="text-muted-foreground">Skipped</p>
                      </div>
                      <div>
                        <p className="font-medium text-red-600">{importProgress.failed.length}</p>
                        <p className="text-muted-foreground">Failed</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setImportModalOpen(false)}>
                  Close and continue
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="csv-file">CSV File</Label>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Select a CSV file containing member information
                  </p>
                </div>

                {csvFile && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="has-headers"
                      checked={hasHeaders}
                      onChange={(e) => handleHasHeadersChange(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                    />
                    <Label htmlFor="has-headers" className="text-sm font-normal cursor-pointer">
                      First row contains column headings
                    </Label>
                  </div>
                )}

                {csvData.length > 0 && csvHeaders.length > 0 && (
                  <>
                    <div>
                      <Label>Field Mapping</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Map CSV columns to the required fields
                      </p>
                      <div className="space-y-2">
                        <div>
                          <Label htmlFor="firstName-map" className="text-sm">
                            First Name
                          </Label>
                          <Select
                            value={fieldMapping.firstName}
                            onValueChange={(value: string) =>
                              setFieldMapping({ ...fieldMapping, firstName: value })
                            }
                          >
                            <SelectTrigger id="firstName-map">
                              <SelectValue placeholder="Select column" />
                            </SelectTrigger>
                            <SelectContent>
                              {csvHeaders.map((header) => (
                                <SelectItem key={header} value={header}>
                                  {header}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="lastName-map" className="text-sm">
                            Last Name
                          </Label>
                          <Select
                            value={fieldMapping.lastName}
                            onValueChange={(value: string) =>
                              setFieldMapping({ ...fieldMapping, lastName: value })
                            }
                          >
                            <SelectTrigger id="lastName-map">
                              <SelectValue placeholder="Select column" />
                            </SelectTrigger>
                            <SelectContent>
                              {csvHeaders.map((header) => (
                                <SelectItem key={header} value={header}>
                                  {header}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="email-map" className="text-sm">
                            Email Address
                          </Label>
                          <Select
                            value={fieldMapping.email}
                            onValueChange={(value: string) =>
                              setFieldMapping({ ...fieldMapping, email: value })
                            }
                          >
                            <SelectTrigger id="email-map">
                              <SelectValue placeholder="Select column" />
                            </SelectTrigger>
                            <SelectContent>
                              {csvHeaders.map((header) => (
                                <SelectItem key={header} value={header}>
                                  {header}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg bg-muted p-3">
                      <p className="text-sm font-medium mb-1">Preview</p>
                      <p className="text-xs text-muted-foreground">
                        Found {csvData.length} row{csvData.length !== 1 ? 's' : ''} to import
                      </p>
                    </div>
                  </>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setImportModalOpen(false);
                    resetImportForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={
                    importing ||
                    importRunning ||
                    !csvFile ||
                    csvData.length === 0 ||
                    !fieldMapping.firstName ||
                    !fieldMapping.lastName ||
                    !fieldMapping.email
                  }
                >
                  {importing ? 'Starting import...' : 'Import Members'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}

